import { useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { aiGatewayService } from '@/services/aiGateway';
import type {
  ScribeChunkReceivedPayload,
  ScribeErrorPayload,
  ScribeSoapNote,
  ScribeSoapResultPayload,
  ScribeTranscriptionPartialPayload,
  ScribeTranscriptionPreviewPayload,
} from '@/services/aiGateway/scribeEvents';
import {
  enqueueChunk,
  markChunkSent,
  getPendingChunks,
  countPendingChunks,
  clearSessionChunks,
} from '@/services/audioChunkStore';
import { drainIdbToRest } from '@/services/audioChunkDrain';
import { recordingSessionApi } from '@/services/recordingSessionApi';
import { useMyProfile } from '@/hooks/useMyProfile';
import {
  startCapture,
  type CaptureSource,
  type CapturePreview,
  MicrophoneInsecureContextError,
  MicrophonePermissionDeniedError,
} from '@/services/audio/captureSource';
import { openAppPermissionSettings } from '@/services/audio/micPermission';
import {
  startMicForegroundService,
  stopMicForegroundService,
} from '@/native/foregroundMic';
import { markTabAsRecorder } from '@/lib/recordingTabMarker';
import { useVoiceStore } from '@/stores/useVoiceStore';
import { usePlatformFlag } from '@/config/platformFlags';

/**
 * Live recording hook — wires the browser mic into the existing AI Gateway
 * scribe pipeline (the same Socket.IO infra that VoiceToSoapRecorder uses).
 *
 * Outputs:
 *   - `amplitude`: 0..1 RMS for a fat waveform indicator (60fps via rAF)
 *   - `bars`: smoothed frequency-bin amplitudes for a richer bar chart
 *   - `segments`: live transcript segments (speaker-labelled when the
 *     server emits diarization fields)
 *   - `connected`: gateway socket state
 *   - `ready`: scribe session reported `connection-ready`
 *
 * Pass `sessionId` (recording session UUID) + optional `patientId`. The
 * hook starts capturing immediately, streams chunks every CHUNK_MS, and
 * tears everything down on unmount or when `enabled` flips false.
 */

export interface TranscriptSegment {
  /** Stable id (chunkIndex or generated). */
  id: string;
  text: string;
  /** Optional speaker label from diarization (e.g., "doctor", "patient", "speaker_1"). */
  speaker?: string;
  /** Server-side chunk index (when available). */
  chunkIndex?: number;
  /** Wall-clock timestamp when the segment arrived at the client. */
  receivedAt: number;
  /** True for fast-preview text (dual-cadence pass): undiarized, shown in
   *  a muted "still transcribing" style, replaced in place by the
   *  canonical diarized partial covering the same audio. Never persisted. */
  pending?: boolean;
}

/** Ephemeral fast-preview segment (SCRIBE_FAST_PARTIALS). Kept separate
 *  from the canonical list so reconciliation (drop-when-covered) can't
 *  ever evict real diarized text. */
interface PreviewSegment {
  id: string;
  text: string;
  /** Canonical 30s chunkIndex whose audio window contains this preview. */
  coversChunkIndex?: number;
  receivedAt: number;
}

interface UseLiveRecordingArgs {
  sessionId: string | null | undefined;
  /**
   * Backend `recording_sessions.id` paired with this socket session. When
   * supplied, the gateway seeds its transcription buffer from the row's
   * existing transcript on connect (so a refresh / cross-device handover
   * picks up where the consult left off) and persists each new partial
   * back to the row. Pass the same id for the lifetime of the session.
   */
  recordingSessionId?: string | null;
  patientId?: string | null;
  /**
   * Template id to bias SOAP synthesis against (slice C consumes it on the
   * backend). Forwarded as-is on `scribe.start-session`. When omitted the
   * gateway falls back to the default SOAP shape.
   */
  templateId?: string | null;
  language?: string;
  /**
   * Capture mode. 'dictate' → the session runs WITHOUT diarization (single
   * voice, faster, no speaker labels); 'visit' (default) keeps the diarized
   * dual-cadence. Forwarded as `diarize` on `scribe.start-session`.
   */
  captureMode?: 'visit' | 'dictate';
  enabled: boolean;
  paused: boolean;
  /**
   * Pre-existing segments to seed the live transcript with on mount —
   * typically the persisted view from a previous browser tab/session,
   * restored from the recording-session DTO. New partials append after.
   */
  initialSegments?: TranscriptSegment[];
  /**
   * Fired once when the consult has been silent (no speech) for
   * SILENCE_TERMINATE_MS. The dock wires this to its Complete handler so a
   * room left recording with nobody talking finalizes whatever WAS said
   * instead of capturing dead air indefinitely. Doctors get a visible
   * countdown (via `silenceCountdown`) before this fires and can cancel it
   * with `extendSilence()`.
   */
  onInactivityTimeout?: () => void;
  /**
   * Per-tenant sustained-silence auto-terminate budget, in milliseconds. When
   * omitted, falls back to SILENCE_TERMINATE_MS (5 min). `0` (or negative)
   * disables the silence auto-terminate entirely — used for long/therapy
   * sessions where deliberate silences are expected. The visible countdown
   * still runs for the last minute before the budget.
   */
  silenceTimeoutMs?: number;
}

export interface ScribeSoapResult {
  medicalRecordId?: string | null;
  patientId?: string | null;
  soapNote?: ScribeSoapNote;
  transcription?: string;
  raw?: ScribeSoapResultPayload;
}

interface UseLiveRecordingResult {
  amplitude: number;
  bars: number[];
  segments: TranscriptSegment[];
  fullText: string;
  connected: boolean;
  ready: boolean;
  error: string | null;
  /** Count of captured chunks still `pending` in IndexedDB — i.e. not yet
      confirmed durable on the server. Non-zero while offline is expected
      (the queue drains on reconnect); a persistently high count while
      *connected* means the socket ack / REST fallback path is failing and
      the doctor should be told before they walk away from the device. */
  pendingChunks: number;
  /** True while the mic is producing effectively no signal (dead / muted /
      wrong device). Surface as a persistent "we can't hear you" banner. */
  noAudioWarning: boolean;
  /** Seconds left before sustained-silence auto-terminate, or null when not
      counting down. Pair with `extendSilence` for a "Keep recording" action. */
  silenceCountdown: number | null;
  /** Reset the silence timers (dismiss the countdown / no-audio warning).
      Wire to the "Keep recording" button. */
  extendSilence: () => void;
  /** Stop audio capture and ask the gateway to finalize the SOAP note.
      Resolves with the SOAP result (incl. medicalRecordId when the gateway
      persists one). */
  endRecording: () => Promise<ScribeSoapResult>;
  /** Recovery path. Called by the dock on mount when it detects a stuck
      session (status=paused|stopped, no medicalRecordId, no soapDraft, but
      with a persisted completion intent + IDB chunks). Re-binds the scribe
      session, replays the audio queue, and re-emits scribe.end-recording —
      the global soap-result listener handles cleanup + navigate. Idempotent:
      no-ops if a finalize is already in flight. Resolves with a status
      describing the path taken so callers can surface typed errors instead
      of staring at an overlay that never finishes. */
  driveRecovery: () => Promise<DriveRecoveryResult>;
}

export type DriveRecoveryStatus =
  | 'socket-replayed'
  | 'rest-reprocessed'
  | 'no-audio'
  | 'rate-limited'
  | 'synth-abandoned'
  | 'rest-drain-failed'
  | 'reprocess-failed'
  | 'skipped-finalizing';

export interface DriveRecoveryResult {
  status: DriveRecoveryStatus;
  /** Server-typed error code when the path ended in a 4xx. */
  code?: string;
  /** Human-readable message suitable for a toast. */
  message?: string;
  /** Seconds the server asked us to wait, when status='rate-limited'. */
  retryAfterSeconds?: number;
}

const BAR_COUNT = 36;

/**
 * If `scribe.chunk-received { uploaded: true }` doesn't arrive within
 * this window after we emit a chunk over the socket, the REST fallback
 * fires to PUT the chunk directly. Picked to be longer than a healthy
 * gateway round-trip (chunk arrival + MinIO write + ack ~ 200-500 ms
 * typical) but short enough that a stalled socket doesn't cost us a
 * full 30 s rotation cycle of "is the audio safe yet".
 */
const CHUNK_ACK_TIMEOUT_MS = 10_000;

// MediaRecorder gotcha: when you use `start(timeslice)`, only the FIRST
// emitted chunk contains the WebM container header (EBML init segment).
// Every subsequent chunk is just cluster bytes and ffmpeg can't decode it
// as a standalone file. The proven workaround (used by the original
// VoiceToSoapRecorderWebSocket) is to *rotate* recorders: every CYCLE_MS,
// spawn a new MediaRecorder against the same MediaStream, let it overlap
// the old one for OVERLAP_MS so we don't drop audio at the boundary, then
// stop the old recorder. Each `onstop` flushes a complete, standalone
// WebM blob that the gateway/ffmpeg can decode end-to-end.
//
// 30 s chunks. We tried 15 s on 2026-04-30 to make first-text feel
// snappier (~25 s vs ~50 s on CPU large-v3) but real consults
// degraded: the 3D-Speaker diarizer needs enough audio per call to
// build stable speaker embeddings, and at 15 s those embeddings
// collapsed adjacent speakers into one cluster. The downstream
// `_diarized_segments_payload` merge step (HEIDI.LLM stt_processor.py)
// then concatenated their sentences into a single chip — looking from
// the doctor's seat like "diarization is broken" or "everything merged
// together". 30 s was 90% accurate before the change; that's the bar.
// DO NOT lower this without a server-side fix that gives the diarizer
// more context (e.g. rolling window across chunks).
const CYCLE_MS = 30_000;

// Dual-cadence fast pass (flag SCRIBE_FAST_PARTIALS). A second rotating
// MediaRecorder on the SAME MediaStream flushes small sub-chunks that the
// gateway transcribes with `is_final=false` — Whisper only, no diarization,
// no persistence — so text lands fast instead of ~35–50s. The canonical 30s
// pipeline above is untouched: when its diarized partial for chunk k arrives,
// every preview covering window ≤ k is dropped and the speaker-labelled text
// takes its place. This respects the DO-NOT-LOWER constraint on CYCLE_MS
// (diarization still sees full 30s windows) while fixing perceived latency.
// Web-only: native capture can't run two recorders, so the phone keeps the
// 30s cadence.
//
// 5s (was 10s): halves time-to-first-text so live capture feels responsive,
// not sluggish. Each preview is a cheap Whisper-only pass (no diarization, no
// persistence) so the extra cadence is modest GPU. Drop further (~3s) only if
// the STT pool has headroom; truly-instant would need the growing-buffer
// re-transcribe the assistant voice mode uses (bigger change).
const PREVIEW_CYCLE_MS = 5_000;

// ── Silence / no-audio safety rails ─────────────────────────────────────────
// Amplitude is the 0..1 RMS value produced in tick() (rms * 4, clamped).
// The auto-terminate timer keys off NEAR-SILENCE (0.02), not "no speech":
// deliberately conservative so a quiet-but-active consult (soft talking, room
// tone, an exam pause) keeps the timer alive — anything above 0.02 resets it.
// It only fires when the mic genuinely hears next to nothing, i.e. an empty
// room / forgotten or dead mic, which is the case we actually want to stop.
const SILENCE_AMP_THRESHOLD = 0.02;
// Below this there's effectively NO signal at all — a dead / muted / wrong
// mic, distinct from a silent-but-working room.
const MIC_DEAD_AMP_THRESHOLD = 0.01;
// Sustained no-signal before we warn the doctor the mic isn't being heard.
const NO_AUDIO_WARN_MS = 5_000;
// Sustained near-silence before the auto-terminate countdown appears, and the
// total before we finalize. Long fuse (4 min → 1-min visible "Keep recording"
// countdown → end at 5 min) so it can't surprise an active consult; the 1-hour
// hard cap is the real backstop. Whatever WAS said still gets a SOAP on end.
// Visible "ends in 1:00 — Keep recording" window before auto-finalize.
const SILENCE_COUNTDOWN_MS = 60_000;
const SILENCE_TERMINATE_MS = 300_000; // 5 min default → auto-finalize
const SILENCE_WARN_MS = SILENCE_TERMINATE_MS - SILENCE_COUNTDOWN_MS; // 4 min → countdown

const monoNow = (): number =>
  typeof performance !== 'undefined' ? performance.now() : Date.now();

export const useLiveRecording = ({
  sessionId,
  recordingSessionId,
  patientId,
  templateId,
  // Transcription language for the consult. When the caller doesn't pass one
  // (the normal case), it resolves from the doctor's `sttLanguage` voice pref
  // below — default English, with 'ar'/'auto' selectable in Settings. 'auto'
  // = bilingual auto-detect (HEIDI normalizes 'auto' → None), which is less
  // accurate at language ID.
  language,
  captureMode,
  enabled,
  paused,
  initialSegments,
  onInactivityTimeout,
  silenceTimeoutMs,
}: UseLiveRecordingArgs): UseLiveRecordingResult => {
  const [amplitude, setAmplitude] = useState(0);
  // True while the mic is producing effectively no signal (dead / muted /
  // wrong device). Cleared the moment audio returns.
  const [noAudioWarning, setNoAudioWarning] = useState(false);
  // Seconds remaining before sustained-silence auto-terminate, or null when
  // not in the warning window. Drives the dock's "ends in N s — Keep
  // recording" countdown.
  const [silenceCountdown, setSilenceCountdown] = useState<number | null>(null);
  const [bars, setBars] = useState<number[]>(() => new Array(BAR_COUNT).fill(0));
  const [segments, setSegments] = useState<TranscriptSegment[]>(
    () => initialSegments ?? [],
  );
  // Fast-preview segments (dual-cadence). Rendered after the canonical
  // segments in a muted style; reconciled away as diarized partials land.
  const [previewSegments, setPreviewSegments] = useState<PreviewSegment[]>([]);
  // IDB chunks not yet confirmed durable on the server. Refreshed after
  // every enqueue/ack and on a slow interval; drives the backlog chip.
  const [pendingChunks, setPendingChunks] = useState(0);
  // The dock's `initialSegments` is derived from `session.transcript`
  // and that field arrives in two waves: first from Zustand's
  // localStorage hydrate (often empty if the prior tab closed before
  // the FE store flushed), then from the post-mount `rehydrate()`
  // server fetch with the real persisted text. The useState
  // initializer above only consumes the first wave, so reload after a
  // capture used to leave the live transcript pane blank even though
  // the server had the full transcript on file.
  //
  // We reapply the seed each time `initialSegments` changes — but only
  // while our own segments array is still empty, so a partial that
  // raced ahead of the rehydrate (or any subsequent live segment) is
  // never clobbered by a later server-snapshot replay. Tracking the
  // seed-applied state per-sessionId means a fresh consult after a
  // completed one re-seeds correctly.
  const seededForSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId) return;
    if (!initialSegments || initialSegments.length === 0) return;
    if (seededForSessionRef.current === sessionId) return;
    setSegments((current) => {
      // Functional update so we read the latest segments without
      // forward-referencing segmentsRef (declared further down). If
      // live partials already populated the pane, don't overwrite —
      // just mark as seeded so a later transcript update doesn't try
      // again for this session.
      seededForSessionRef.current = sessionId;
      return current.length > 0 ? current : initialSegments;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, initialSegments]);
  const [connected, setConnected] = useState(aiGatewayService.isConnected());
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture source — abstracts web (MediaRecorder rotating) vs native
  // (capacitor-voice-recorder). Owns mic acquisition, chunk production,
  // and pause/resume; the hook keeps owning the gateway socket, IDB
  // replay, and the visualizer wired to whatever stream the source exposes
  // (web only — native returns null and the visualizer flat-lines).
  const captureRef = useRef<CaptureSource | null>(null);
  // Generation token to disambiguate parallel start() calls. Incremented
  // by teardown so any in-flight startCapture (still awaiting getUserMedia
  // / IDB replay) can detect a stale completion and discard its capture
  // instead of registering it under captureRef. Pre-fix: React StrictMode's
  // double-mount of the start/stop useEffect fired start() #1, cleanup
  // immediately, then start() #2 — both startCaptures resolved, each set
  // captureRef.current, but only the last was tracked. The orphaned first
  // recorder kept rotating chunks every 30 s, doubling every transcription
  // emitted to the live pane and to SOAP synth.
  const startGenerationRef = useRef(0);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  // Silence / no-audio tracking. monoNow() timestamps of the last frame that
  // crossed each amplitude threshold; both reset on (re)start, resume, and
  // extendSilence so paused / dismissed time never accrues toward a timeout.
  // The mirror refs let tick() (60fps) push React state only on change.
  const lastSignalAtRef = useRef(0);
  const lastVoiceAtRef = useRef(0);
  const silenceFiredRef = useRef(false);
  const noAudioWarningRef = useRef(false);
  const silenceCountdownRef = useRef<number | null>(null);
  // Latest callback without re-arming the start/stop effect.
  const onInactivityTimeoutRef = useRef(onInactivityTimeout);
  onInactivityTimeoutRef.current = onInactivityTimeout;
  // Per-tenant silence budget, read live by tick() via refs so a late-arriving
  // limit (react-query) isn't stale-closured. terminateMs<=0 disables the
  // auto-terminate; warnMs is the 1-min countdown window before it.
  const silenceTerminateMsRef = useRef(SILENCE_TERMINATE_MS);
  const silenceWarnMsRef = useRef(SILENCE_WARN_MS);
  const resolvedTerminateMs =
    silenceTimeoutMs != null && silenceTimeoutMs >= 0
      ? silenceTimeoutMs
      : SILENCE_TERMINATE_MS;
  silenceTerminateMsRef.current = resolvedTerminateMs;
  silenceWarnMsRef.current =
    resolvedTerminateMs > 0
      ? Math.max(0, resolvedTerminateMs - SILENCE_COUNTDOWN_MS)
      : 0;
  const resetSilenceTracking = useCallback(() => {
    const now = monoNow();
    lastSignalAtRef.current = now;
    lastVoiceAtRef.current = now;
    silenceFiredRef.current = false;
    if (noAudioWarningRef.current) {
      noAudioWarningRef.current = false;
      setNoAudioWarning(false);
    }
    if (silenceCountdownRef.current !== null) {
      silenceCountdownRef.current = null;
      setSilenceCountdown(null);
    }
  }, []);
  const extendSilence = useCallback(() => {
    resetSilenceTracking();
  }, [resetSilenceTracking]);
  const chunkIndexRef = useRef(0);
  const sessionIdRef = useRef<string | null>(null);
  const startedRef = useRef(false);
  // Per-chunk watchdog timers. If a `scribe.chunk-received` ack with
  // `uploaded: true` doesn't arrive within CHUNK_ACK_TIMEOUT_MS after
  // we emit, the watchdog fires the REST upload as a backup so the
  // chunk reaches MinIO even if the socket silently dropped the emit.
  // Cleared by the chunk-received listener when the ack lands.
  const chunkWatchdogs = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  // True from the moment the doctor presses Complete until the gateway
  // confirms a `scribe.soap-result` (or the doctor abandons). When the
  // socket reconnects mid-finalize, the connection-change handler uses
  // this to re-fire `scribe.end-recording` from where the prior socket
  // died — without it, the dock would sit on "Generating note…" forever.
  const finalizingRef = useRef(false);
  // Hard gate for late `onChunk` callbacks after teardown/discard. The
  // rotating recorder can still flush a pending stop callback after the
  // UI path has already ended the session; when that happened we were
  // still enqueueing/sending orphaned chunks into a socket session the
  // backend had already torn down.
  const acceptChunksRef = useRef(true);
  /** In-flight `capture.stop()` (resolves only after its final chunk has
   *  been handed to onChunk). endRecording awaits this so it can't
   *  finalize the session before the tail audio has been sent. */
  const captureStopRef = useRef<Promise<void> | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const patientIdRef = useRef<string | null | undefined>(patientId);
  patientIdRef.current = patientId;
  // Resolve the transcription language: explicit arg wins, else the doctor's
  // backend-synced scribe-language pref, else English.
  const sttLanguagePref = useVoiceStore((s) => s.settings.sttLanguage);
  const effectiveLanguage = language ?? sttLanguagePref ?? 'en';
  const languageRef = useRef<string>(effectiveLanguage);
  languageRef.current = effectiveLanguage;
  const recordingSessionIdRef = useRef<string | null | undefined>(
    recordingSessionId,
  );
  recordingSessionIdRef.current = recordingSessionId;
  const templateIdRef = useRef<string | null | undefined>(templateId);
  templateIdRef.current = templateId;
  // Capture mode → whether the pipeline diarizes. Read via ref so every
  // (re-)emit of scribe.start-session (reconnect, resume) sees the current
  // value. 'dictate' → diarize:false (single voice, faster).
  const captureModeRef = useRef<'visit' | 'dictate' | undefined>(captureMode);
  captureModeRef.current = captureMode;
  // Higher-accuracy first draft: wait for the refined full-audio transcript
  // before the note. Read via ref so every (re-)emit of scribe.start-session
  // carries the current value.
  const preferRefinedForNote = useVoiceStore(
    (s) => s.settings.preferRefinedForNote,
  );
  const preferRefinedRef = useRef<boolean>(preferRefinedForNote);
  preferRefinedRef.current = preferRefinedForNote;
  // Pull the doctor's specialty so the gateway can pick the right
  // SOAP prompt + hotword pack server-side. Falls through to gateway-side
  // defaults (user.specialty → 'general') if the profile isn't loaded yet.
  const myProfile = useMyProfile();
  const specialtyRef = useRef<string | null | undefined>(
    myProfile.data?.specialty,
  );
  specialtyRef.current = myProfile.data?.specialty;

  // Dual-cadence preview flag — read via ref inside start() so the value
  // is fresh at capture (re)start without re-arming the pipeline effects
  // on a mid-consult flag flip.
  const fastPartialsEnabled = usePlatformFlag('SCRIBE_FAST_PARTIALS');
  const fastPartialsRef = useRef(fastPartialsEnabled);
  fastPartialsRef.current = fastPartialsEnabled;
  // Previews are emitted by the canonical recorder (captureSource fans
  // them out), so there is no separate loop to tear down — but the hook
  // still needs to *mute* them at moments where late-arriving preview
  // text would be wrong on screen: paused dock, mid-finalize under the
  // synth overlay, post-teardown. start() unmutes.
  const previewSuppressedRef = useRef(false);
  // Monotonic preview counter for the whole hook lifetime — dedupe key on
  // the receive side, independent of the canonical chunk index.
  const previewCounterRef = useRef(0);
  const stopPreviewLoop = useCallback(() => {
    previewSuppressedRef.current = true;
  }, []);

  // Refresh the pending-IDB-chunk count (backlog chip). Best-effort: IDB
  // being unavailable (private mode) just freezes the count at its last
  // value, which starts at 0.
  const refreshPendingCount = useCallback(async (sid?: string | null) => {
    const target = sid ?? sessionIdRef.current;
    if (!target) {
      setPendingChunks(0);
      return;
    }
    try {
      setPendingChunks(await countPendingChunks(target));
    } catch {
      /* IDB unavailable — leave the count alone */
    }
  }, []);

  // Keep latest segments accessible to event handlers without re-subscribing.
  const segmentsRef = useRef<TranscriptSegment[]>([]);
  segmentsRef.current = segments;

  // Distinguishes "first time we ever saw `connected`" from "socket
  // dropped and came back". Without it the connection-change handler
  // re-fires `scribe.start-session` on the initial connect that just
  // followed `start()`'s own emit — duplicating every session start.
  // Production logs showed 5–6 start-sessions per consult because of
  // this; the gateway then opened that many overlapping HEIDI upstream
  // connections, each of which got cancelled when the next one
  // displaced it, and the SOAP LLM stream never completed.
  const everConnectedRef = useRef(false);
  // True once `scribe.start-session` has been successfully emitted on
  // the *current* socket. Flips back to false on disconnect so the next
  // connect re-registers. Distinct from `everConnectedRef` so a cold
  // start-while-offline (start() failed to register because no socket)
  // can be recovered when the socket finally lands, instead of only
  // recovering on a *re*-connect after a previously-good session.
  const sessionRegisteredRef = useRef(false);

  // ── Subscribe to gateway events once (independent of a session) ──
  useEffect(() => {
    aiGatewayService.connect().catch((err) => {
      // Being unreachable is NOT an error state the user should see as
      // "Mic error" — it just means we're offline. The connection chip
      // already reflects this as "Offline · saving" / "Connecting…", and
      // a started recorder keeps capturing to IDB to sync on reconnect.
      console.warn('[useLiveRecording] gateway connect failed (offline?)', err);
    });
    const offConn = aiGatewayService.onConnectionChange((s) => {
      const isConn = s === 'connected';
      const isReconnect = isConn && everConnectedRef.current;
      if (isConn) everConnectedRef.current = true;
      // Disconnect drops the gateway-side session record, so a fresh
      // connect needs a fresh start-session emit regardless of whether
      // it counts as a "reconnect".
      if (!isConn) sessionRegisteredRef.current = false;
      setConnected(isConn);
      // (Re-)bind the scribe session when the socket arrives if the
      // recorder is mid-flight and we don't already have a registered
      // session on this socket. Covers two cases the prior `isReconnect`
      // gate missed: (a) cold-start while offline — start() couldn't
      // emit because no socket existed, and we need the FIRST connect
      // to register the session; (b) the original reconnect case where
      // the session was registered on the prior socket but needs to be
      // re-registered on the new one.
      if (
        isConn &&
        startedRef.current &&
        sessionIdRef.current &&
        !endingRef.current &&
        !sessionRegisteredRef.current
      ) {
        try {
          aiGatewayService.scribeStartSession({
            sessionId: sessionIdRef.current,
            recordingSessionId: recordingSessionIdRef.current ?? undefined,
            patientId: patientIdRef.current ?? undefined,
            templateId: templateIdRef.current ?? undefined,
            specialty: specialtyRef.current ?? undefined,
            language: languageRef.current,
            soapSection: 'full',
            transcriptionOnly: false,
            diarize: captureModeRef.current !== 'dictate',
            preferRefinedForNote: preferRefinedRef.current,
          });
          sessionRegisteredRef.current = true;
        } catch { /* ignore — next chunk will retry implicitly */ }
      }
      // Voiding the `isReconnect` reference so the linter doesn't flag it
      // — kept in scope above for readability of the gate logic.
      void isReconnect;
      // Recovery: socket came back while the doctor's Complete was still
      // in flight. Replay any IDB chunks the dead socket never flushed,
      // then re-emit `scribe.end-recording` so the gateway runs synth
      // against the full audio. The global `scribe.soap-result` listener
      // (set up in RecordingDock) handles the navigate-to-review side.
      if (isConn && finalizingRef.current && sessionIdRef.current) {
        const sid = sessionIdRef.current;
        void (async () => {
          try {
            const pending = await getPendingChunks(sid);
            for (const row of pending) {
              try {
                const buf = await row.blob.arrayBuffer();
                aiGatewayService.scribeSendAudioChunk({
                  type: 'audio-chunk',
                  audioData: buf,
                  sessionId: sid,
                  chunkIndex: row.chunkIndex,
                });
                await markChunkSent(sid, row.chunkIndex);
              } catch (err) {
                console.warn(
                  '[useLiveRecording] reconnect-replay failed for chunk',
                  row.chunkIndex,
                  err,
                );
                return; // bail; next reconnect will pick up where we stopped
              }
            }
            aiGatewayService.scribeEndRecording({
              type: 'end-recording',
              sessionId: sid,
            });
          } catch (err) {
            console.warn('[useLiveRecording] reconnect-finalize failed', err);
          }
        })();
      }
    });
    const offReady = aiGatewayService.on('scribe.connection-ready', () => {
      setReady(true);
    });
    // The event fires once per socket connect. When the socket was
    // already warm before this hook mounted (e.g. /welcome pre-connects,
    // then the dock mounts on /home), it will never re-fire — seed from
    // the latched state so the dock doesn't sit on "Warming up".
    if (aiGatewayService.isScribeReady()) setReady(true);
    const offError = aiGatewayService.on(
      'scribe.error',
      (data: ScribeErrorPayload) => {
        // A `retriable` error is a "still connecting to the voice service"
        // state, not a mic failure — the backend couldn't confirm a live
        // STT path yet but the upstream is auto-reconnecting. Treat it like
        // being offline: don't show a scary error (capture keeps flowing to
        // IDB + REST fallback), just reflect not-ready so the UI shows the
        // "connecting" chip. `ready` re-fires via scribe.connection-ready
        // once the path lands (and on the next socket reconnect cycle).
        if (data?.retriable) {
          setReady(false);
          return;
        }
        setError(data?.message ?? 'Scribe error');
      },
    );
    // Per-chunk durable-on-server ack. Slice 0 contract: gateway emits
    // `scribe.chunk-received` with `{chunkIndex, uploaded}` after the
    // MinIO mirror upsert succeeds. We only flip the IDB row to `sent`
    // on `uploaded: true` so a successful socket emit followed by a
    // MinIO failure still leaves the chunk pending for retry.
    //
    // Pre-Slice-0 gateways either omit `uploaded` or set it to false —
    // in that case fall back to the previous optimistic behaviour
    // (treat the ack itself as proof of server-side queue presence).
    const offChunkReceived = aiGatewayService.on(
      'scribe.chunk-received',
      (data: ScribeChunkReceivedPayload) => {
        const sid = data?.sessionId;
        if (!sid) return;
        if (sessionIdRef.current && sid !== sessionIdRef.current) return;
        if (typeof data.chunkIndex !== 'number') return;
        if (data.uploaded === false) return; // mirror not durable yet
        // Cancel the REST watchdog for this chunk — the gateway has
        // confirmed durability, no need to fall back.
        const wd = chunkWatchdogs.current.get(data.chunkIndex);
        if (wd) {
          clearTimeout(wd);
          chunkWatchdogs.current.delete(data.chunkIndex);
        }
        void markChunkSent(sid, data.chunkIndex)
          .catch(() => {})
          .finally(() => void refreshPendingCount(sid));
      },
    );
    // Single, definitive trigger for "this consult's audio is done" —
    // wired here (not in endRecording's per-request callback) so a
    // reconnect-driven re-emit also reliably drops the IDB queue. The
    // per-request callback in endRecording resolves the same event for
    // the same-tab navigate path, but it goes away on socket teardown,
    // which is exactly the recovery case we have to keep the queue for.
    const offSoapForCleanup = aiGatewayService.on(
      'scribe.soap-result',
      (data: ScribeSoapResultPayload) => {
        const sid = data?.sessionId;
        if (!sid) return;
        if (sessionIdRef.current && sid !== sessionIdRef.current) return;
        finalizingRef.current = false;
        setPreviewSegments([]);
        void clearSessionChunks(sid)
          .catch(() => {})
          .finally(() => void refreshPendingCount(sid));
      },
    );
    const offPartial = aiGatewayService.on(
      'scribe.transcription-partial',
      (data: ScribeTranscriptionPartialPayload) => {
        // Both RecordingDock and RecordOnPhoneModal subscribe to this
        // event on the same socket. Drop partials that belong to a
        // different surface so the dock doesn't paint phone-session
        // text (or vice versa) when the doctor switches between them.
        if (
          data?.sessionId &&
          sessionIdRef.current &&
          data.sessionId !== sessionIdRef.current
        ) {
          return;
        }
        const next: TranscriptSegment[] = [];
        // Server may either return a flat `text`/`speaker` or a `segments`
        // array (when diarization splits a chunk into multiple turns).
        if (Array.isArray(data?.segments) && data.segments.length > 0) {
          data.segments.forEach((s, i) => {
            const text = (s?.text ?? '').trim();
            if (!text) return;
            next.push({
              id: `${data.chunkIndex ?? Date.now()}-${i}`,
              text,
              speaker: s.speaker,
              chunkIndex: data.chunkIndex,
              receivedAt: Date.now(),
            });
          });
        } else {
          const text = (data?.text ?? '').trim();
          if (text) {
            next.push({
              id: `${data?.chunkIndex ?? Date.now()}-0`,
              text,
              speaker: data?.speaker,
              chunkIndex: data?.chunkIndex,
              receivedAt: Date.now(),
            });
          }
        }
        if (next.length > 0) {
          setSegments((prev) => {
            // Drop server-marked dupes (same chunkIndex+speaker+text seen twice).
            const seen = new Set(
              prev.map((s) => `${s.chunkIndex}|${s.speaker ?? ''}|${s.text}`),
            );
            const fresh = next.filter(
              (n) => !seen.has(`${n.chunkIndex}|${n.speaker ?? ''}|${n.text}`),
            );
            return fresh.length === 0 ? prev : [...prev, ...fresh];
          });
        }
        // Reconcile the fast-preview pane: the canonical (diarized) text
        // for chunk k has landed, so every preview whose audio window is
        // covered by ≤ k is now redundant — the speaker-labelled version
        // replaces it in place. Later previews (windows > k) stay until
        // their own canonical partial arrives.
        if (typeof data?.chunkIndex === 'number') {
          const covered = data.chunkIndex;
          setPreviewSegments((prev) =>
            prev.length === 0
              ? prev
              : prev.filter((p) => (p.coversChunkIndex ?? Infinity) > covered),
          );
        }
      },
    );
    // Fast-preview text (dual-cadence pass). Ephemeral display-only —
    // dedupe by previewIndex, session-scoped like the partial handler.
    const offPreview = aiGatewayService.on(
      'scribe.transcription-preview',
      (data: ScribeTranscriptionPreviewPayload) => {
        if (
          data?.sessionId &&
          sessionIdRef.current &&
          data.sessionId !== sessionIdRef.current
        ) {
          return;
        }
        const text = (data?.text ?? '').trim();
        if (!text) return;
        const id = `preview-${data.previewIndex ?? Date.now()}`;
        const covers = data.coversChunkIndex;
        setPreviewSegments((prev) => {
          if (prev.some((p) => p.id === id)) return prev;
          const next = {
            id,
            text,
            coversChunkIndex: covers,
            receivedAt: Date.now(),
          };
          // Each preview is a GROWING PREFIX of its 30s window (one
          // recorder, re-transcribed as it fills), so a new preview for
          // window k is a superset of the previous one — it replaces it.
          // Appending instead made the pane repeat the same sentence with
          // one more clause each time.
          if (typeof covers === 'number') {
            return [...prev.filter((p) => p.coversChunkIndex !== covers), next];
          }
          return [...prev, next];
        });
      },
    );

    // Tab close / hard navigation: best-effort tell the backend to free
    // its scribe buffers before the socket dies on its own. Socket.IO
    // doesn't support sendBeacon, but emitting here gives the OS a
    // microtask to flush over the still-open transport. Failure is
    // non-fatal — backend disconnect cleanup will eventually catch it.
    const handleBeforeUnload = () => {
      if (
        startedRef.current &&
        sessionIdRef.current &&
        !endingRef.current &&
        aiGatewayService.isConnected()
      ) {
        endingRef.current = true;
        try {
          aiGatewayService.scribeEndRecording({
            type: 'end-recording',
            sessionId: sessionIdRef.current,
          });
        } catch {
          /* socket teardown beat us — that path triggers backend GC anyway */
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      offConn();
      offReady();
      offError();
      offPartial();
      offPreview();
      offSoapForCleanup();
      offChunkReceived();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Slow-poll the pending-chunk backlog while a session is live. The
  // enqueue/ack paths refresh eagerly; this interval is the safety net for
  // paths that bypass them (REST drain, another tab, IDB GC).
  useEffect(() => {
    if (!enabled || !sessionId) {
      setPendingChunks(0);
      return;
    }
    void refreshPendingCount(sessionId);
    const id = setInterval(() => void refreshPendingCount(sessionId), 10_000);
    return () => clearInterval(id);
  }, [enabled, sessionId, refreshPendingCount]);

  // ── Start/stop capture pipeline based on `enabled` + `sessionId` ──
  useEffect(() => {
    if (!enabled || !sessionId) {
      teardown();
      startedRef.current = false;
      sessionIdRef.current = null;
      setReady(false);
      return;
    }
    if (startedRef.current && sessionIdRef.current === sessionId) {
      return; // already running for this session
    }
    startedRef.current = true;
    sessionIdRef.current = sessionId;
    // New session: clear any flags carried over from a prior session in
    // the same hook instance (e.g. doctor finished one consult and
    // started another without unmounting the dock). Without this reset
    // the connection-change handler stays gated by the prior session's
    // `endingRef = true` and never re-binds on a real reconnect, and
    // the seed-tracker would think we're already seeded for the new
    // session and skip the rehydrated transcript.
    endingRef.current = false;
    seededForSessionRef.current = null;
    chunkIndexRef.current = 0;
    setSegments([]);
    setPreviewSegments([]);
    setError(null);
    void start();

    return () => {
      teardown();
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, sessionId]);

  // ── Pause / resume: fully release the mic on pause so the browser's
  // mic-in-use indicator drops (paused === we are not listening, by
  // construction). On resume we re-run the full start() pipeline, which
  // re-acquires getUserMedia, rebuilds the AnalyserNode, and spins up a
  // fresh rotating MediaRecorder. The scribe socket stays open across
  // pause/resume so the gateway treats it as one continuous session.
  // Trade-off: resume incurs a one-time getUserMedia round-trip (~50 ms
  // typical, ~500 ms worst case on cold permission cache); the upside is
  // that paused state is now indistinguishable from "mic off" to both
  // the user and the OS-level indicator. ──
  useEffect(() => {
    if (!enabled || !sessionId) return;
    if (paused) {
      // Flush the in-flight tail chunk to the gateway before releasing the
      // mic (so the transcript catches up to the pause click), then tear down
      // everything mic-related. startedRef stays true so the scribe session
      // and chunk-index continuity are preserved across the pause window.
      void pauseMicWithFlush();
    } else if (startedRef.current && !captureRef.current) {
      void start();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, enabled, sessionId]);

  const start = useCallback(async () => {
    // Snapshot the generation BEFORE any await. If teardown runs while
    // we're inside an await below, it bumps the ref — and the post-await
    // checks (after startCapture in particular) bail out before storing
    // the capture or wiring the visualizer to its stream.
    const startGen = ++startGenerationRef.current;
    try {
      acceptChunksRef.current = true;
      // Try to connect to the AI Gateway opportunistically, but DO NOT
      // gate mic capture on it. Pre-fix, a cold-start under a flaky
      // network would early-return here — the recorder never opened and
      // every second of audio the doctor produced was dropped on the
      // floor with no IDB trace. Now: surface the error chip, then fall
      // through to mic acquisition. Each chunk's onChunk handler still
      // enqueues to IDB before any send attempt, and the REST fallback
      // inside the watchdog handles the no-socket case directly. The
      // connection-change subscription re-fires scribe.start-session and
      // replays IDB chunks the moment the socket comes back.
      try {
        await aiGatewayService.connect();
      } catch (err) {
        // Don't surface this as an error — offline is an expected state.
        // The connection chip shows "Offline · saving" and we fall through
        // to mic capture so nothing is lost while the gateway is unreachable.
        console.warn('[useLiveRecording] gateway connect failed (offline?)', err);
        // Fall through — keep capturing audio to IDB so nothing is lost
        // while the gateway is unreachable.
      }

      // Replay any chunks we durably enqueued in a prior page lifetime
      // BEFORE opening a fresh recorder. Sequential to preserve order and
      // not slam the gateway's chunk queue. Doing the replay first also
      // means the next live chunk's index picks up after the highest
      // replayed one, so the gateway sees a strictly monotonic sequence.
      //
      // Skip the replay when the session mounts in `paused` state. Pause
      // means the doctor isn't actively capturing, so the gateway doesn't
      // need the audio yet, AND the IDB queue should stay intact for the
      // resume — replaying here would re-send chunks the gateway already
      // saw during the original live capture (per-chunk transcribe runs
      // again, doubling Whisper load) and the user would see ghost audio
      // chunks streaming over the socket on a "paused" home page —
      // exactly the behaviour the doctor flagged ("no recorder, still
      // seeing audio chunks"). The pause→resume effect re-runs start()
      // which re-enters this path with pausedRef.current=false and the
      // replay fires there at the right moment.
      if (!pausedRef.current) {
        try {
          const pending = await getPendingChunks(sessionId!);
          if (pending.length > 0) {
            const maxReplayedIndex = pending[pending.length - 1].chunkIndex;
            chunkIndexRef.current = Math.max(
              chunkIndexRef.current,
              maxReplayedIndex + 1,
            );
            for (const row of pending) {
              try {
                const buf = await row.blob.arrayBuffer();
                aiGatewayService.scribeSendAudioChunk({
                  type: 'audio-chunk',
                  audioData: buf,
                  sessionId: sessionId!,
                  chunkIndex: row.chunkIndex,
                });
                await markChunkSent(sessionId!, row.chunkIndex);
              } catch (err) {
                console.warn(
                  '[useLiveRecording] replay failed for chunk',
                  row.chunkIndex,
                  err,
                );
                break; // stop on first failure; remaining stay pending
              }
            }
          }
        } catch (err) {
          console.warn('[useLiveRecording] pending-chunk replay failed', err);
        }
      }

      // Mounted-paused fast-exit: bind the gateway session below so a
      // resume picks up correctly, but skip mic + capture acquisition
      // entirely. The pause→resume useEffect re-invokes start() with
      // pausedRef.current=false and the full mic+capture pipeline
      // initialises there.
      //
      // Pre-fix, start() always acquired getUserMedia + opened a
      // rotating MediaRecorder regardless of the paused flag. The
      // existing isPaused() gate inside captureSource only suppressed
      // the chunk EMIT, not the mic acquisition — so a doctor on the
      // home page with a paused session would see the OS mic indicator
      // light up briefly on every load.
      if (pausedRef.current) {
        try {
          aiGatewayService.scribeStartSession({
            sessionId: sessionId!,
            recordingSessionId: recordingSessionId ?? undefined,
            patientId: patientId ?? undefined,
            templateId: templateId ?? undefined,
            specialty: specialtyRef.current ?? undefined,
            language: languageRef.current,
            soapSection: 'full',
            transcriptionOnly: false,
            diarize: captureModeRef.current !== 'dictate',
            preferRefinedForNote: preferRefinedRef.current,
          });
          sessionRegisteredRef.current = true;
        } catch (err) {
          // Not connected yet — the onConnectionChange subscription will
          // (re-)emit start-session once the socket lands. Not user-facing.
          console.warn('[useLiveRecording] start-session deferred (offline?)', err);
        }
        return;
      }

      // Hold the Android microphone-typed FGS slot for the lifetime of
      // the consult. No-ops on iOS / web. Started before capture so the
      // OS sees the FGS in place before the first MediaRecorder.start().
      await startMicForegroundService();

      // Acquire the platform-appropriate capture source. The native
      // (Capacitor) impl keeps recording while the screen is locked or
      // app backgrounded — load-bearing for the Siri/Assistant flow.
      //
      const capture = await startCapture({
        cycleMs: CYCLE_MS,
        startingIndex: chunkIndexRef.current,
        isPaused: () => pausedRef.current,
        // Fast partials, fanned out from the ONE canonical recorder.
        // Ephemeral by design: no IDB, no acks, no persistence — a lost
        // preview costs nothing because the 30s chunk carries the same
        // audio. Each preview is a growing prefix of the in-flight cycle,
        // so a later one supersedes the earlier; the pane drops every
        // preview covering a window once that window's diarized partial
        // lands.
        ...(fastPartialsRef.current
          ? {
              previewIntervalMs: PREVIEW_CYCLE_MS,
              onPreview: ({ blob, index }: CapturePreview) => {
                if (previewSuppressedRef.current) return;
                if (!aiGatewayService.isConnected()) return; // skip offline
                void (async () => {
                  try {
                    const buf = await blob.arrayBuffer();
                    aiGatewayService.scribeSendAudioPreview({
                      type: 'audio-preview',
                      audioData: buf,
                      sessionId: sessionId!,
                      previewIndex: previewCounterRef.current++,
                      coversChunkIndex: index,
                    });
                  } catch {
                    /* fire-and-forget — canonical pipeline has the audio */
                  }
                })();
              },
            }
          : {}),
        onChunk: async ({ blob, index }) => {
          if (!acceptChunksRef.current) return;
          // Persist BEFORE attempting send. Even if the socket is down,
          // the chunk is durable and the next mount will replay it.
          chunkIndexRef.current = Math.max(chunkIndexRef.current, index + 1);
          try {
            await enqueueChunk(sessionId!, index, blob);
            void refreshPendingCount(sessionId!);
          } catch (err) {
            console.warn('[useLiveRecording] IDB enqueue failed', err);
            // Fall through — best-effort send so we don't drop the chunk
            // entirely just because IDB is unavailable (private mode, etc.)
          }

          const sid = sessionId!;

          // REST fallback closure — invoked either immediately (socket
          // disconnected at chunk-ready time) or by the watchdog timer
          // after CHUNK_ACK_TIMEOUT_MS without an `uploaded: true` ack.
          const restFallback = async () => {
            try {
              await recordingSessionApi.uploadChunk(sid, index, blob);
              await markChunkSent(sid, index)
                .catch(() => {})
                .finally(() => void refreshPendingCount(sid));
            } catch (err) {
              console.warn(
                '[useLiveRecording] REST fallback upload failed for chunk',
                index,
                err,
              );
              // Stay pending in IDB — driveRecovery / the next session
              // mount will pick it up via drainIdbToRest.
            }
          };

          if (!aiGatewayService.isConnected()) {
            // No socket — go straight to REST. Don't arm a watchdog
            // since there's no socket emit to wait on.
            void restFallback();
            return;
          }

          try {
            const buf = await blob.arrayBuffer();
            aiGatewayService.scribeSendAudioChunk({
              type: 'audio-chunk',
              audioData: buf,
              sessionId: sid,
              chunkIndex: index,
            });
            // Watchdog: if the gateway's `uploaded: true` ack doesn't
            // arrive in time, the REST fallback will. The
            // chunk-received listener clears the timer when the ack
            // lands. Don't markSent here — the listener owns that
            // signal so a successful socket emit followed by a
            // server-side mirror failure still leaves the chunk
            // pending for retry / fallback.
            const prior = chunkWatchdogs.current.get(index);
            if (prior) clearTimeout(prior);
            const wd = setTimeout(() => {
              chunkWatchdogs.current.delete(index);
              void restFallback();
            }, CHUNK_ACK_TIMEOUT_MS);
            chunkWatchdogs.current.set(index, wd);
          } catch (err) {
            console.warn('[useLiveRecording] chunk send failed', err);
            // Socket emit threw synchronously — try REST immediately.
            void restFallback();
          }
        },
        onFatalError: (err) => {
          const msg =
            err instanceof Error ? err.message : 'Microphone capture failed';
          setError(msg);
        },
      });
      // Stale-generation guard: teardown ran (or another start() started)
      // while we were awaiting getUserMedia / chunk replay. Stop the
      // recorder we just opened and return — don't publish it. Without
      // this, a StrictMode double-mount in dev (or any quick re-mount in
      // prod) leaks a second parallel MediaRecorder rotating chunks
      // alongside the tracked one, which is what produced the duplicate
      // transcription-partial events the doctor saw.
      if (startGen !== startGenerationRef.current) {
        void capture.stop().catch(() => {});
        return;
      }
      captureRef.current = capture;
      // This generation owns the mic now — let its previews through
      // (a prior teardown/pause/finalize may have muted them).
      previewSuppressedRef.current = false;
      // Any previous capture's stop has nothing to do with this one;
      // endRecording must not await a resolved promise from a prior
      // generation. (acceptChunksRef is already re-armed at the top of
      // start(), and teardown's finally is generation-guarded.)
      captureStopRef.current = null;
      // Mark this tab as the active recorder. The dock checks for this
      // marker on cold mount to decide whether to auto-acquire the mic
      // (refresh in same tab → marker present → resume) or surface a
      // recovery banner (fresh tab / different device → no marker).
      // Survives refresh, dies with the tab, doesn't leak across tabs.
      if (sessionId) markTabAsRecorder(sessionId);

      // Visualizer — only available when the source exposes a live
      // MediaStream (web). On native (Capacitor), getStream() returns
      // null and we skip the AnalyserNode wiring; the dock falls back to
      // a flat baseline. The pulsing "REC" chip is the indicator there.
      const stream = capture.getStream();
      if (stream) {
        const AC =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const ctx = new AC();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        source.connect(analyser);
        analyserRef.current = analyser;

        const freq = new Uint8Array(analyser.frequencyBinCount);
        const time = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          if (!analyserRef.current) return;
          const nowTs = monoNow();
          analyserRef.current.getByteFrequencyData(freq);
          analyserRef.current.getByteTimeDomainData(time);

          // RMS amplitude
          let sumSq = 0;
          for (let i = 0; i < time.length; i++) {
            const v = (time[i] - 128) / 128;
            sumSq += v * v;
          }
          const rms = Math.sqrt(sumSq / time.length);
          const amp = Math.min(1, rms * 4);

          // While paused, the AnalyserNode is still wired to the live mic
          // — flat-line so the dock matches the explicit "Paused" state.
          if (pausedRef.current) {
            // Don't accrue silence while paused — pin the timers to now and
            // drop any in-progress warning so resume starts the clock fresh.
            lastSignalAtRef.current = nowTs;
            lastVoiceAtRef.current = nowTs;
            if (noAudioWarningRef.current) {
              noAudioWarningRef.current = false;
              setNoAudioWarning(false);
            }
            if (silenceCountdownRef.current !== null) {
              silenceCountdownRef.current = null;
              setSilenceCountdown(null);
            }
            setAmplitude(0);
            setBars((prev) =>
              prev.every((v) => v === 0) ? prev : new Array(BAR_COUNT).fill(0),
            );
            rafRef.current = requestAnimationFrame(tick);
            return;
          }

          setAmplitude(amp);

          // ── Safety-rail detection ──────────────────────────────────────
          if (amp >= MIC_DEAD_AMP_THRESHOLD) lastSignalAtRef.current = nowTs;
          if (amp >= SILENCE_AMP_THRESHOLD) lastVoiceAtRef.current = nowTs;

          // Zero-audio (mic dead) warning — reflects current sustained
          // no-signal, clears the instant any signal returns.
          const wantNoAudio = nowTs - lastSignalAtRef.current >= NO_AUDIO_WARN_MS;
          if (wantNoAudio !== noAudioWarningRef.current) {
            noAudioWarningRef.current = wantNoAudio;
            setNoAudioWarning(wantNoAudio);
          }

          // Sustained-silence auto-terminate countdown. Budget is per-tenant
          // (refs, read live). terminateMs<=0 disables it entirely (e.g. long
          // therapy sessions) — clear any countdown and skip.
          const terminateMs = silenceTerminateMsRef.current;
          const warnMs = silenceWarnMsRef.current;
          if (terminateMs <= 0) {
            if (silenceCountdownRef.current !== null) {
              silenceCountdownRef.current = null;
              setSilenceCountdown(null);
            }
          } else {
            const silentMs = nowTs - lastVoiceAtRef.current;
            if (silentMs >= terminateMs) {
              if (!silenceFiredRef.current) {
                silenceFiredRef.current = true;
                if (silenceCountdownRef.current !== null) {
                  silenceCountdownRef.current = null;
                  setSilenceCountdown(null);
                }
                onInactivityTimeoutRef.current?.();
              }
            } else if (silentMs >= warnMs) {
              const remaining = Math.max(
                0,
                Math.ceil((terminateMs - silentMs) / 1000),
              );
              if (remaining !== silenceCountdownRef.current) {
                silenceCountdownRef.current = remaining;
                setSilenceCountdown(remaining);
              }
            } else if (silenceCountdownRef.current !== null) {
              silenceCountdownRef.current = null;
              setSilenceCountdown(null);
            }
          }

          const next = new Array<number>(BAR_COUNT);
          const step = Math.floor(freq.length / BAR_COUNT) || 1;
          for (let i = 0; i < BAR_COUNT; i++) {
            let acc = 0;
            for (let j = 0; j < step; j++) acc += freq[i * step + j] || 0;
            const v = acc / step / 255;
            next[i] = Math.min(1, v * 1.6);
          }
          setBars(next);

          rafRef.current = requestAnimationFrame(tick);
        };
        // Start the silence clock fresh for this capture window.
        resetSilenceTracking();
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Native capture (capacitor-voice-recorder) exposes no MediaStream
        // and the plugin has no metering API — real levels are unknowable
        // here. A flat-lined waveform read as "the mic is broken" to
        // doctors, so drive a deliberately stylized breathing wave instead:
        // smooth traveling pulse, modest amplitude, obviously an animation
        // rather than fake level metering. Paused shows flat, matching the
        // web path. Silence safety rails stay disabled on native (no
        // analyser to measure with) — unchanged from before.
        const t0 = monoNow();
        const syntheticTick = () => {
          if (captureRef.current !== capture) return; // torn down / rotated
          if (pausedRef.current) {
            setAmplitude(0);
            setBars((prev) =>
              prev.every((v) => v === 0) ? prev : new Array(BAR_COUNT).fill(0),
            );
            rafRef.current = requestAnimationFrame(syntheticTick);
            return;
          }
          const t = (monoNow() - t0) / 1000;
          const next = new Array<number>(BAR_COUNT);
          for (let i = 0; i < BAR_COUNT; i++) {
            // Two out-of-phase travelling sines + a slow breath envelope —
            // organic-looking, cheap, and clearly periodic.
            const travel = Math.sin(t * 2.4 - i * 0.34) * 0.5 + 0.5;
            const ripple = Math.sin(t * 3.1 + i * 0.52) * 0.5 + 0.5;
            const breathe = Math.sin(t * 0.8) * 0.5 + 0.5;
            next[i] = 0.1 + (travel * 0.6 + ripple * 0.4) * (0.16 + breathe * 0.22);
          }
          setBars(next);
          setAmplitude(0.22 + (Math.sin(t * 1.6) * 0.5 + 0.5) * 0.12);
          rafRef.current = requestAnimationFrame(syntheticTick);
        };
        rafRef.current = requestAnimationFrame(syntheticTick);
      }

      // Fast-preview is no longer a recorder of its own — it is fanned
      // out from the canonical recorder inside startCapture (onPreview
      // below). See the note there: a second MediaRecorder on the same
      // MediaStream is what killed the canonical pipeline on WebKit.

      // Start the AI Gateway scribe session. ensureConnected() inside the
      // helper still throws if the socket dropped between the await above
      // and now, so we catch it and surface as a recoverable error.
      try {
        // transcriptionOnly:false — the gateway will emit live partial
        // transcriptions DURING capture (with diarization speaker labels
        // when heidi.llm provides them), AND on `scribe.end-recording` it
        // will run the full SOAP synthesis pipeline. The hook's
        // `endRecording()` resolves with the resulting medicalRecordId.
        aiGatewayService.scribeStartSession({
          sessionId: sessionId!,
          recordingSessionId: recordingSessionId ?? undefined,
          patientId: patientId ?? undefined,
          templateId: templateId ?? undefined,
          specialty: specialtyRef.current ?? undefined,
          language: languageRef.current,
          soapSection: 'full',
          transcriptionOnly: false,
          diarize: captureModeRef.current !== 'dictate',
          preferRefinedForNote: preferRefinedRef.current,
        });
        sessionRegisteredRef.current = true;
      } catch (err) {
        // The only reason this throws is "not connected" — i.e. we're
        // offline. That's not an error worth alarming the doctor with:
        // the rotating recorder keeps producing chunks (durably queued to
        // IDB), and the onConnectionChange subscription re-emits
        // start-session + replays the chunks the moment the socket returns.
        console.warn('[useLiveRecording] start-session deferred (offline?)', err);
      }
    } catch (err) {
      if (err instanceof MicrophoneInsecureContextError) {
        // Phones reject getUserMedia outside secure contexts. Without
        // this branch the error landed in the generic toast and made
        // it look like the mic itself was broken — which is the bug
        // clinicians reported on mobile.
        setError('Microphone needs HTTPS');
        toast.error('Microphone needs a secure connection', {
          description:
            "Open this page over https:// (not http://) and try again. Browsers block mic access on insecure pages.",
          duration: 10000,
        });
      } else if (err instanceof MicrophonePermissionDeniedError) {
        setError('Microphone access is blocked');
        toast.error('Microphone access is blocked', {
          description:
            "MAIE can't hear you. Allow microphone access to record consults.",
          duration: 8000,
          action: {
            label: 'Open Settings',
            onClick: () => {
              openAppPermissionSettings();
            },
          },
        });
      } else {
        const msg =
          err instanceof Error ? err.message : 'Could not start recording';
        setError(msg);
      }
      teardown();
    }
  }, [language, patientId, sessionId, recordingSessionId, templateId]);

  // Mic-only teardown: releases the OS mic + analyser + recorder, but
  // leaves `startedRef` and the chunk-index counter alone so a resume can
  // pick up the same scribe session without disturbing chunk continuity.
  const teardownMic = () => {
    stopPreviewLoop();
    // Invalidate any in-flight start() that hasn't yet stored its capture.
    // The post-await guard in start() reads this and bails before
    // captureRef.current = capture, so an orphaned MediaRecorder never
    // gets registered under a tracked slot.
    const gen = ++startGenerationRef.current;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const cap = captureRef.current;
    captureRef.current = null;
    // `acceptChunksRef` must stay TRUE until whichever stop is in flight
    // has handed its final chunk to onChunk — same rule as
    // pauseMicWithFlush. That flush is the ONLY chunk a consult shorter
    // than the 30s rotation ever produces, so clearing the flag early
    // loses the entire consult ("No audio recorded").
    //
    // Two owners are possible and BOTH must be waited on:
    //   - teardown owns it (pause, unmount, session change) → we stop it
    //     here;
    //   - endRecording already took it (Complete runs endRecording first,
    //     then the optimistic `stopped` status disables this hook and
    //     lands us here a render later) → captureRef is already null but
    //     its stop is still flushing.
    const stopping = cap
      ? (captureStopRef.current = cap.stop().catch(() => {}))
      : captureStopRef.current;
    if (stopping) {
      void stopping.finally(() => {
        // A fresh start() ran while we were flushing — leave its
        // pipeline alone.
        if (gen === startGenerationRef.current) {
          acceptChunksRef.current = false;
        }
      });
    } else {
      acceptChunksRef.current = false;
    }
    // Drop the Android FGS so the persistent notification clears.
    void stopMicForegroundService();
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    // Reset the visualizer so a paused dock doesn't leave a phantom
    // last-frame waveform.
    setBars(new Array(BAR_COUNT).fill(0));
    setAmplitude(0);
  };

  // Pause-with-flush: unlike teardownMic (which drops the mic immediately and
  // rejects any late chunk), this KEEPS accepting chunks until the capture
  // source has flushed its final chunk — the audio recorded right up to the
  // pause click. The capture's stop() now resolves only after that tail
  // chunk's onChunk (IDB enqueue + socket emit) has run, so the gateway
  // transcribes it and the segment lands in the live transcript even though
  // we're paused. Without this, ~0.5–5 s of the doctor's last words vanished
  // unless they waited before pausing. Guarded by the start() generation so a
  // quick resume that re-acquires the mic isn't clobbered by our cleanup.
  const pauseMicWithFlush = async () => {
    // Preview pass stops dead on pause — its tail sub-chunk is deliberately
    // dropped (the canonical pause-flush below carries that audio, and a
    // preview arriving during the paused state would paint "live" text on
    // a paused dock).
    stopPreviewLoop();
    const gen = ++startGenerationRef.current;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    const cap = captureRef.current;
    captureRef.current = null;
    // acceptChunksRef stays TRUE across the stop so the flushed tail is kept.
    if (cap) {
      try {
        await cap.stop();
      } catch {
        /* best-effort — the mic is being released regardless */
      }
    }
    // A resume ran while we were flushing (it bumps the generation and spins
    // up a fresh capture) — don't tear down its pipeline.
    if (gen !== startGenerationRef.current) return;
    acceptChunksRef.current = false;
    void stopMicForegroundService();
    if (audioCtxRef.current) {
      void audioCtxRef.current.close().catch(() => {});
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setBars(new Array(BAR_COUNT).fill(0));
    setAmplitude(0);
  };

  // Full teardown for unmount / session-id change. Drops everything
  // teardownMic does plus the started/session refs so a fresh start()
  // is required to bring the pipeline back up.
  //
  // Also tells the backend to free its per-client scribe state. Without
  // this, the gateway's `sessionConfigs` / `audioBuffers` /
  // `transcriptionBuffers` keep accumulating until the underlying socket
  // disconnects — and the singleton `aiGatewayService` keeps the socket
  // open across route changes. Result: a doctor who navigates away mid-
  // consult would leave 30s+ of audio + transcript pinned in backend RAM
  // for the lifetime of their browser tab.
  //
  // We deliberately route through the existing `scribe.end-recording`
  // event (instead of inventing a `scribe.cancel-session`): the backend
  // path already handles the empty-buffer case and any partial transcript
  // gets persisted to the recording_sessions row, which is decent crash
  // recovery. `endingRef` guards against double-firing when a normal
  // endRecording() ran first.
  const endingRef = useRef(false);
  const teardown = () => {
    teardownMic();
    sessionRegisteredRef.current = false;
    // Drop any outstanding chunk-ack watchdogs — they'd otherwise fire
    // a REST upload after we're done and after the chunks have
    // already been confirmed durable via the soap-result cleanup.
    for (const wd of chunkWatchdogs.current.values()) clearTimeout(wd);
    chunkWatchdogs.current.clear();
    if (
      startedRef.current &&
      sessionIdRef.current &&
      !endingRef.current &&
      aiGatewayService.isConnected()
    ) {
      endingRef.current = true;
      try {
        aiGatewayService.scribeEndRecording({
          type: 'end-recording',
          sessionId: sessionIdRef.current,
        });
      } catch {
        /* socket may be mid-disconnect; backend will GC on its own */
      }
    }
  };

  // Canonical text only — previews are display scaffolding and must never
  // leak into anything that reads like "the transcript".
  const fullText = segments
    .map((s) => (s.speaker ? `${s.speaker}: ${s.text}` : s.text))
    .join(' ');

  // Merged view for the live pane: canonical segments first, then the
  // not-yet-diarized previews (they always cover strictly newer audio) in
  // their muted pending style.
  const mergedSegments =
    previewSegments.length === 0
      ? segments
      : [
          ...segments,
          ...previewSegments.map<TranscriptSegment>((p) => ({
            id: p.id,
            text: p.text,
            chunkIndex: p.coversChunkIndex,
            receivedAt: p.receivedAt,
            pending: true,
          })),
        ];

  const endRecording = useCallback((): Promise<ScribeSoapResult> => {
    return new Promise((resolve, reject) => {
      const sid = sessionIdRef.current;
      if (!sid) {
        teardown();
        reject(new Error('No active recording session'));
        return;
      }
      // Caller-driven end. Block teardown's auto-end so an immediately-
      // following unmount (common: caller awaits this Promise then
      // navigates) doesn't fire a second `scribe.end-recording`. Also
      // flip startedRef false so a socket churn during the SOAP wait
      // doesn't trip the connection-change handler into emitting a
      // fresh `scribe.start-session` against the session we just told
      // the gateway to finalize. The pre-2026-05-03 behaviour kept
      // startedRef true until safeSettle (which runs on soap-result or
      // the 120s timeout), so any reconnect inside that window —
      // including the routine 30s upstream health-check churn — would
      // resurrect the session and cancel the in-flight LLM stream.
      endingRef.current = true;
      startedRef.current = false;

      const proceedAfterFlush = () => {
        acceptChunksRef.current = false;
        // Previews are display-only scaffolding for the live pane; the
        // finalize path works from the canonical chunks, so drop them the
        // moment the doctor completes.
        setPreviewSegments([]);
        // Stop the visualizer + audio context. The capture source already
        // released the mic on stop().
        if (rafRef.current != null) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        if (audioCtxRef.current) {
          void audioCtxRef.current.close().catch(() => {});
          audioCtxRef.current = null;
        }
        analyserRef.current = null;
        resetSilenceTracking();

        // Mark finalize-in-flight so a socket reconnect mid-synth re-fires
        // `scribe.end-recording` (see connection-change handler above). The
        // global `scribe.soap-result` listener flips this back to false on
        // confirmed success; until then the dock keeps showing "Generating".
        finalizingRef.current = true;

        const result: ScribeSoapResult = {};
        let settled = false;
        const settleSuccess = () => {
          if (settled) return;
          settled = true;
          startedRef.current = false;
          sessionIdRef.current = null;
          // IDB cleanup happens in the global soap-result listener — don't
          // duplicate it here (and crucially, don't run it on the timeout
          // path, which used to wipe the queue and lose recovery audio).
          resolve(result);
        };
        // The same-tab Complete path resolves via the per-request callback
        // below. If the callback never arrives — socket dropped after we
        // emitted, gateway hasn't processed yet — we fall back to a 5-min
        // soft wait, then resolve with whatever we have. The dock's
        // synth-in-flight state survives this resolve (driven by the
        // session row's status + the global soap-result listener), so the
        // doctor still sees "Generating note…" and the recovery driver
        // takes over once the backend is back.
        const timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          // Leave finalizingRef ARMED and IDB queue INTACT — the dock's
          // synth-in-flight state is driven by session.status + draftPresent,
          // and a future reconnect re-fires end-recording.
          resolve(result);
        }, 300_000);

        aiGatewayService.scribeEndRecording(
          { type: 'end-recording', sessionId: sid },
          (event, data) => {
            if (event === 'scribe.soap-result' || event === 'scribe.soap-complete') {
              result.medicalRecordId = data?.medicalRecordId ?? data?.recordId;
              result.patientId = data?.patientId;
              result.soapNote = data?.soapNote ?? data?.soap;
              result.transcription = data?.transcription ?? data?.voiceTranscript;
              result.raw = data;
              clearTimeout(timeout);
              settleSuccess();
            } else if (event === 'scribe.error') {
              clearTimeout(timeout);
              if (settled) return;
              settled = true;
              startedRef.current = false;
              sessionIdRef.current = null;
              reject(new Error(data?.message ?? 'Scribe error'));
            }
          },
        );
      };

      // Stop the capture source first — its stop() resolves AFTER the
      // final tail chunk has flowed through onChunk (so the gateway sees
      // the consult's last 30 s before we finalize). Tiny grace window
      // before end-recording so the socket flushes the chunk emit.
      // The preview loop dies immediately: a preview arriving mid-finalize
      // would paint "live" text under the synth overlay.
      stopPreviewLoop();
      const cap = captureRef.current;
      captureRef.current = null;
      if (cap) {
        captureStopRef.current = cap
          .stop()
          .catch(() => {})
          .then(() => {
            void stopMicForegroundService();
            setTimeout(proceedAfterFlush, 200);
          });
      } else {
        void stopMicForegroundService();
        // No capture here usually means teardownMic already took it —
        // Complete flips the session to `stopped`, which disables the
        // hook and tears the mic down before this runs. That stop is
        // still flushing its tail chunk, so finalizing now would tell
        // the gateway to synthesize audio that hasn't arrived yet.
        const inflight = captureStopRef.current;
        if (inflight) {
          void inflight.then(() => setTimeout(proceedAfterFlush, 200));
        } else {
          proceedAfterFlush();
        }
      }
    });
  }, []);

  const driveRecovery = useCallback(async (): Promise<DriveRecoveryResult> => {
    const sid = sessionId ?? sessionIdRef.current;
    if (!sid) return { status: 'skipped-finalizing' };
    // Already finalizing in this tab — the existing flow (or its
    // reconnect retry) will land first; piling on a parallel attempt just
    // duplicates chunk emits and confuses the gateway's session state.
    if (finalizingRef.current) return { status: 'skipped-finalizing' };

    // Try the socket path first — it gives the doctor live transcript
    // previews while synth runs, which is nicer than the silent REST
    // path. If the connect fails, fall through to the REST drain +
    // reprocess fallback (Slice 1C).
    let socketReady = false;
    try {
      await aiGatewayService.connect();
      socketReady = aiGatewayService.isConnected();
    } catch {
      socketReady = false;
    }

    if (socketReady) {
      sessionIdRef.current = sid;
      finalizingRef.current = true;
      try {
        // Re-bind the scribe session so the gateway accepts our chunks +
        // the end-recording emit against this id. Without this, a fresh
        // socket has no in-memory session config and the chunks are dropped.
        aiGatewayService.scribeStartSession({
          sessionId: sid,
          recordingSessionId: recordingSessionIdRef.current ?? sid,
          patientId: patientIdRef.current ?? undefined,
          templateId: templateIdRef.current ?? undefined,
          specialty: specialtyRef.current ?? undefined,
          language: languageRef.current,
          soapSection: 'full',
          transcriptionOnly: false,
          diarize: captureModeRef.current !== 'dictate',
          preferRefinedForNote: preferRefinedRef.current,
        });
        const pending = await getPendingChunks(sid);
        for (const row of pending) {
          try {
            const buf = await row.blob.arrayBuffer();
            aiGatewayService.scribeSendAudioChunk({
              type: 'audio-chunk',
              audioData: buf,
              sessionId: sid,
              chunkIndex: row.chunkIndex,
            });
            await markChunkSent(sid, row.chunkIndex);
          } catch (err) {
            console.warn(
              '[useLiveRecording] driveRecovery socket replay failed',
              row.chunkIndex,
              err,
            );
            // Switch tracks: socket replay is failing — fall back to REST
            // drain + reprocess so we don't sit here forever.
            socketReady = false;
            break;
          }
        }
        if (socketReady) {
          aiGatewayService.scribeEndRecording({
            type: 'end-recording',
            sessionId: sid,
          });
          return { status: 'socket-replayed' };
        }
      } catch (err) {
        console.warn('[useLiveRecording] driveRecovery socket path failed', err);
        socketReady = false;
      }
    }

    // Slice 1C — REST fallback. Drain pending IDB chunks via the
    // chunk-upload endpoint, then call the reprocess endpoint to run
    // SOAP synth server-side. No live transcript previews on this
    // path; the dock's polling effect picks up the soapDraft when
    // it lands.
    sessionIdRef.current = sid;
    finalizingRef.current = true;
    try {
      const drain = await drainIdbToRest(sid);
      if (drain.failed > 0 && drain.uploaded === 0) {
        // Total drain failure (auth, network, etc.). Leave finalizingRef
        // armed so the next reconnect path retries.
        console.warn(
          '[useLiveRecording] REST drain failed before reprocess',
          drain.firstError,
        );
        return {
          status: 'rest-drain-failed',
          message:
            drain.firstError?.message ?? 'Audio upload failed',
        };
      }
      await recordingSessionApi.reprocess(sid);
      // The reprocess response includes medicalRecordId on success.
      // The dock's HTTP polling effect (RecordingDock) then sees the
      // soapDraft + medicalRecordId on the session row and navigates.
      return { status: 'rest-reprocessed' };
    } catch (err) {
      console.warn('[useLiveRecording] driveRecovery REST path failed', err);
      const e = err as {
        response?: {
          status?: number;
          data?: {
            code?: string;
            message?: string;
            retryAfterSeconds?: number;
          };
        };
        message?: string;
      };
      const code = e?.response?.data?.code;
      const msg = e?.response?.data?.message ?? e?.message;
      if (code === 'AUDIO_NOT_ON_SERVER') {
        return { status: 'no-audio', code, message: msg };
      }
      if (code === 'SYNTH_ABANDONED') {
        return { status: 'synth-abandoned', code, message: msg };
      }
      if (code === 'REPROCESS_RATE_LIMITED') {
        return {
          status: 'rate-limited',
          code,
          message: msg,
          retryAfterSeconds: e?.response?.data?.retryAfterSeconds,
        };
      }
      return { status: 'reprocess-failed', code, message: msg };
    }
  }, [sessionId]);

  return {
    amplitude,
    bars,
    segments: mergedSegments,
    fullText,
    connected,
    ready,
    error,
    pendingChunks,
    noAudioWarning,
    silenceCountdown,
    extendSilence,
    endRecording,
    driveRecovery,
  };
};
